// Reusable badge + metadata for the five Part types (per spec/14 "Part types"
// table + v8 mockup's "+ Add Part flow" Step B color/icon scheme).
//
// Single source of truth for:
//   - badge rendering (used by PartsPage cards, part type picker, future
//     quote builder part chips)
//   - PART_TYPE_META — exported map used by the Quote builder agent + the
//     part wizard (driven by wizardSteps per type)
//
// Mirrors the visual contract of TierBadge: three sizes (sm/md/lg), icon
// optional via showLabel=false, hover tooltip carries the long description.
// Tailwind classes only; palette is project-standard (blue/purple/amber/
// green/steel — see tailwind.config.ts).

import type { PartType } from '@/shared/crmTypes';

export interface PartTypeBadgeProps {
  type: PartType;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export interface PartTypeMeta {
  type: PartType;
  /** Long human label — "Purchased / Component", "Cut Blank", etc. */
  label: string;
  /** Short label used in tight pills (cards, chips). */
  shortLabel: string;
  /** One-line description shown in the picker and on hover. */
  description: string;
  /** Emoji glyph from the v8 mockup. */
  icon: string;
  /** Tailwind palette key (blue|purple|amber|green|steel|teal). Lets callers
   *  build their own classes off the same source — e.g. the picker's left
   *  stripe uses bg-<colorClass>, the badge uses bg-<colorClass>/15. */
  colorClass: 'blue' | 'purple' | 'amber' | 'green' | 'steel' | 'teal';
  /** Ordered list of wizard step names (per spec/14 "Part types" table). */
  wizardSteps: string[];
}

export const PART_TYPE_META: Record<PartType, PartTypeMeta> = {
  machined: {
    type: 'machined',
    label: 'Machined',
    shortLabel: 'Machined',
    description: 'Bar/block stock + machine setups, runtime tiers.',
    icon: '📐',
    colorClass: 'blue',
    wizardSteps: ['Identity', 'Material', 'Setups', 'Finishing', 'Review'],
  },
  cut_blank: {
    type: 'cut_blank',
    label: 'Cut Blank',
    shortLabel: 'Cut Blank',
    description: 'Sheet + outside cut (laser / waterjet / plasma).',
    icon: '⚡',
    colorClass: 'purple',
    wizardSteps: ['Identity', 'Sheet stock', 'DXF / Vendor', 'Finishing', 'Review'],
  },
  weldment: {
    type: 'weldment',
    label: 'Weldment',
    shortLabel: 'Weldment',
    description: 'Multiple bodies joined — sub-parts + weld ops + finishing.',
    icon: '🔥',
    colorClass: 'amber',
    wizardSteps: ['Identity', 'Sub-parts', 'Weld ops', 'Finishing', 'Review'],
  },
  hybrid: {
    type: 'hybrid',
    label: 'Hybrid',
    shortLabel: 'Hybrid',
    description: 'Cut blank + secondary machine ops + finishing.',
    icon: '🧩',
    colorClass: 'green',
    wizardSteps: ['Identity', 'Sheet stock', 'DXF', 'Secondary ops', 'Finishing', 'Review'],
  },
  purchased: {
    type: 'purchased',
    label: 'Purchased / Component',
    shortLabel: 'Purchased',
    description: 'McMaster, Misumi, etc. Just source + markup.',
    icon: '📦',
    colorClass: 'steel',
    wizardSteps: ['Source', 'Markup', 'Review'],
  },
  collar: {
    type: 'collar',
    // Renamed from "Collar / Frame" → "Sheet Material" — covers the four shape
    // variants (frame, rectangle, circle, standoff) now driven by the Nesting
    // step's shape picker. The PartType enum value stays 'collar' for back-
    // compat with existing data; only the human label changed.
    label: 'Sheet Material',
    shortLabel: 'Sheet',
    description: 'Sheet stock cuts — collars, frames, rectangular, circular, standoffs.',
    icon: '🖼',
    colorClass: 'teal',
    wizardSteps: ['Identity', 'Material', 'Nesting', 'Cut time', 'Finishing', 'Review'],
  },
  turninsulation: {
    type: 'turninsulation',
    label: 'Turn Insulation',
    shortLabel: 'Turn Insul',
    description: 'B-stage cured tape between motor coil turns — KMF electric-motor heritage.',
    icon: '🌀',
    colorClass: 'purple',
    wizardSteps: ['Identity', 'Material', 'Review'],
  },
};

// Static class tables — Tailwind needs literal class names to tree-shake,
// so we can't template `bg-${color}/15`. Keeps the meta colorClass key
// portable for callers that build their own variants.
const BADGE_CLASSES: Record<PartTypeMeta['colorClass'], string> = {
  blue: 'bg-blue/15 text-blue border border-blue/30',
  purple: 'bg-purple/15 text-purple border border-purple/30',
  amber: 'bg-amber/15 text-amber border border-amber/30',
  green: 'bg-green/15 text-green border border-green/30',
  steel: 'bg-steel/15 text-steel border border-steel/30',
  teal: 'bg-teal/15 text-teal border border-teal/30',
};

const SIZE_CLASSES: Record<NonNullable<PartTypeBadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 gap-0.5',
  md: 'text-xs px-2 py-0.5 gap-1',
  lg: 'text-sm px-2.5 py-1 gap-1.5',
};

// Defensive fallback for parts whose type isn't one of the 5 known values
// (imported records, schema drift, typos). One bad row would otherwise
// crash the whole list / wizard host via React unmount. Mirrors TierBadge.
const UNKNOWN_META: PartTypeMeta = {
  type: 'machined', // arbitrary — never read, just satisfies the type
  label: 'Unknown',
  shortLabel: 'Unknown',
  description: 'Part type not recognized — fix on the part detail page',
  icon: '?',
  colorClass: 'steel',
  wizardSteps: [],
};
const UNKNOWN_BADGE_CLS = 'bg-bg text-text-mute border border-line-dark';

export function PartTypeBadge({ type, size = 'md', showLabel = true }: PartTypeBadgeProps) {
  const meta = PART_TYPE_META[type] ?? UNKNOWN_META;
  const sizeCls = SIZE_CLASSES[size];
  const badgeCls = PART_TYPE_META[type] ? BADGE_CLASSES[meta.colorClass] : UNKNOWN_BADGE_CLS;

  // sm always icon-only (mirrors TierBadge contract).
  const renderLabel = showLabel && size !== 'sm';

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${sizeCls} ${badgeCls}`}
      title={`${meta.label} — ${meta.description}`}
      aria-label={`${meta.label} part type`}
    >
      <span aria-hidden>{meta.icon}</span>
      {renderLabel && <span>{meta.shortLabel}</span>}
    </span>
  );
}
